import { Box, createStyles, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import {
  find,
  flatten,
  forEach,
  includes,
  isArray,
  isFunction,
  keys,
  map,
  reverse,
  some,
} from 'lodash/fp';
import React, { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useCurrentUser } from '@portals/api/ui';
import {
  useActionDispatch,
  useActiveHub,
  useIsSidebarOpen,
} from '@portals/redux';
import { setActiveHub } from '@portals/redux/actions/ui';
import { VerticalScrollBar } from '@portals/scrollbar';
import { HubType } from '@portals/types';

import { HUB_LABEL, HUBS_ROUTES_GROUPS } from './hubs-sidebar.constants';
import { HubsSidebarSection } from './HubsSidebarSection';
import { useAppConfig } from '../../../../context';
import { useCommonConfig } from '../../../../hooks/portal-config';
import { usePermissionAccess } from '../../../permission-access/use-permission-access';
import { SidebarFooter } from '../SidebarFooter';

export function HubsSidebar() {
  const { classes, cx } = useStyles();

  const activeHub = useActiveHub();
  const isSidebarOpen = useIsSidebarOpen();
  const { routes, extraLayout } = useAppConfig();
  const { isAdmin } = usePermissionAccess();
  const location = useLocation();
  const setActiveHubType = useActionDispatch(setActiveHub);
  const currentUser = useCurrentUser();

  const configuration = useCommonConfig();

  const allRoutes = useMemo(
    () =>
      reverse(flatten(map(({ childRoutes }) => childRoutes, routes.dashboard))),
    [routes.dashboard]
  );

  useEffect(() => {
    const currentRoute = find(({ path }) => {
      let adjustedPath = path;

      if (isFunction(adjustedPath)) {
        adjustedPath = adjustedPath({
          configuration: configuration.data,
          isAdmin,
        });
      }

      if (!isArray(adjustedPath)) {
        try {
          return Boolean(
            matchPath(
              {
                path: adjustedPath,
                end: true,
                caseSensitive: true,
              },
              location.pathname
            )
          );
        } catch (e) {
          return false;
        }
      }

      return some((currentPath) => {
        try {
          return Boolean(
            matchPath(
              {
                path: currentPath,
                end: true,
              },
              location.pathname
            )
          );
        } catch (e) {
          return;
        }
      }, adjustedPath);
    }, allRoutes);

    if (!currentRoute) return;

    const currentRouteId = currentRoute.id;
    const currentHubRoutes = HUBS_ROUTES_GROUPS[activeHub];

    if (!includes(currentRouteId, currentHubRoutes)) {
      const allHubs = keys(HUBS_ROUTES_GROUPS);

      forEach((hubType: HubType) => {
        const hubRoutes = HUBS_ROUTES_GROUPS?.[hubType];

        if (includes(currentRouteId, hubRoutes)) {
          setActiveHubType(hubType);
          return false;
        }
      }, allHubs);
    }
  }, [
    activeHub,
    allRoutes,
    configuration.data,
    location.pathname,
    isAdmin,
    setActiveHubType,
  ]);

  const getHubLabel = () => {
    if (activeHub === HubType.Home) {
      return `Welcome ${currentUser?.data?.name}`;
    }

    return HUB_LABEL[activeHub];
  };

  return (
    <motion.div layout className={classes.container}>
      <Box className={cx('sidebar-content', classes.sidebarContent)} bg="white">
        <VerticalScrollBar
          hideTracksWhenNotNeeded
          renderView={(props) => <div className="sidebar-nav" {...props} />}
        >
          <Box py="xl" px="md">
            {isSidebarOpen ? (
              <Text px="md" color="blue_gray.3">
                {getHubLabel()}
              </Text>
            ) : null}
          </Box>

          <HubsSidebarSection />

          {extraLayout?.sidebarLayout
            ? extraLayout?.sidebarLayout({ configuration: configuration.data })
            : null}
        </VerticalScrollBar>

        <SidebarFooter />
      </Box>
    </motion.div>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    height: '100%',
  },
  sidebarContent: {
    height: '100%',
    display: 'grid',
    gridTemplateRows: '1fr 56px',
    borderRight: `2px solid ${theme.colors.blue_gray[0]}`,

    '.sidebar-nav': {
      paddingLeft: 0,
      listStyle: 'none',
      paddingBottom: '15px',
    },
  },
}));
